/**
 * Hook pour gérer les paiements clients d'un chantier
 * Inclut les paiements, ajustements (avenants/remises), et totaux calculés
 * Supporte le temps réel via Supabase Realtime
 *
 * Migré vers React Query (useQuery + useMutation)
 */

import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { captureError } from '@/lib/sentry';
import { queryKeys } from '@/lib/queryKeys';
import { invalidateFinancesChantier } from '@/lib/queryInvalidation';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { queueOrExecuteInvalidation } from '@/lib/modalProtection';
import type {
  PaiementClient,
  AjustementPaiement,
  PaiementTotals,
  PaiementFormData,
  AjustementFormData,
  FactureClientSimple,
  UsePaiementsChantierReturn,
  StatutPaiementChantier,
} from '@/types/paiement';


/**
 * Hook principal pour gérer les paiements d'un chantier
 */
export function usePaiementsChantier(chantierId: string | undefined): UsePaiementsChantierReturn {
  const { entrepriseId } = useSubscription();
  const queryClient = useQueryClient();
  const enabled = !!entrepriseId && !!chantierId;

  // ============================================================================
  // QUERIES
  // ============================================================================

  /**
   * Query 1 : Paiements du chantier
   */
  const paiementsQuery = useQuery({
    queryKey: queryKeys.finances.paiements(entrepriseId!, chantierId!),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('paiements_clients')
        .select('*')
        .eq('chantier_id', chantierId!)
        .order('date_paiement', { ascending: false });

      if (error) {
        console.error('Erreur fetch paiements:', error);
        throw error;
      }

      return (data || []) as PaiementClient[];
    },
    enabled,
  });

  /**
   * Query 2 : Ajustements du chantier
   */
  const ajustementsQuery = useQuery({
    queryKey: queryKeys.finances.ajustements(entrepriseId!, chantierId!),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ajustements_paiement')
        .select('*')
        .eq('chantier_id', chantierId!)
        .order('date_ajustement', { ascending: false });

      if (error) {
        console.error('Erreur fetch ajustements:', error);
        throw error;
      }

      return (data || []) as AjustementPaiement[];
    },
    enabled,
  });

  /**
   * Query 3 : Totaux calculés via la fonction SQL
   */
  const totalsQuery = useQuery({
    queryKey: queryKeys.finances.paiementTotals(entrepriseId!, chantierId!),
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('get_paiement_totals', { p_chantier_id: chantierId! });

      if (error) {
        console.error('Erreur fetch totals:', error);
        throw error;
      }

      if (data && data.length > 0) {
        return data[0] as PaiementTotals;
      }

      // Valeurs par défaut si la fonction ne retourne rien
      return {
        total_devis: 0,
        total_ajustements: 0,
        total_a_encaisser: 0,
        total_encaisse: 0,
        reste_a_encaisser: 0,
        pourcentage_encaisse: 0,
        statut_paiement: 'non_facture' as StatutPaiementChantier,
      } as PaiementTotals;
    },
    enabled,
  });

  /**
   * Query 4 : Factures clients (pour le select de liaison)
   */
  const facturesQuery = useQuery({
    queryKey: queryKeys.finances.facturesClients(entrepriseId!, chantierId!),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('factures_clients')
        .select('id, numero_facture, montant_ht, montant_ttc, date_emission, statut')
        .eq('chantier_id', chantierId!)
        .order('date_emission', { ascending: false });

      if (error) {
        console.error('Erreur fetch factures clients:', error);
        // Ne pas bloquer si les factures ne sont pas accessibles
        return [] as FactureClientSimple[];
      }

      return (data || []) as FactureClientSimple[];
    },
    enabled,
  });

  // ============================================================================
  // REALTIME SUBSCRIPTIONS
  // ============================================================================

  useEffect(() => {
    if (!chantierId || !entrepriseId) return;

    // Subscription aux changements de paiements_clients
    const paiementsChannel = supabase
      .channel(`paiements_${chantierId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'paiements_clients',
          filter: `chantier_id=eq.${chantierId}`,
        },
        () => {
          // Si une modal de saisie est ouverte, mettre en file d'attente
          queueOrExecuteInvalidation(() => {
            queryClient.invalidateQueries({
              queryKey: queryKeys.finances.paiements(entrepriseId, chantierId),
            });
            queryClient.invalidateQueries({
              queryKey: queryKeys.finances.paiementTotals(entrepriseId, chantierId),
            });
          });
        }
      )
      .subscribe();

    // Subscription aux changements d'ajustements_paiement
    const ajustementsChannel = supabase
      .channel(`ajustements_${chantierId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'ajustements_paiement',
          filter: `chantier_id=eq.${chantierId}`,
        },
        () => {
          // Si une modal de saisie est ouverte, mettre en file d'attente
          queueOrExecuteInvalidation(() => {
            queryClient.invalidateQueries({
              queryKey: queryKeys.finances.ajustements(entrepriseId, chantierId),
            });
            queryClient.invalidateQueries({
              queryKey: queryKeys.finances.paiementTotals(entrepriseId, chantierId),
            });
          });
        }
      )
      .subscribe();

    // Cleanup
    return () => {
      supabase.removeChannel(paiementsChannel);
      supabase.removeChannel(ajustementsChannel);
    };
  }, [chantierId, entrepriseId, queryClient]);

  // ============================================================================
  // MUTATIONS - PAIEMENTS
  // ============================================================================

  /**
   * Mutation : ajouter un paiement
   */
  const addPaiementMutation = useMutation({
    mutationFn: async (data: PaiementFormData) => {
      const insertData = {
        chantier_id: chantierId!,
        montant: data.montant,
        date_paiement: data.date_paiement,
        type: data.type,
        moyen_paiement: data.moyen_paiement || null,
        reference: data.reference || null,
        statut: data.statut || 'encaisse',
        notes: data.notes || null,
        facture_client_id: data.facture_client_id || null,
        devis_id: data.devis_id || null,
        fichier_url: data.fichier_url || null,
        extraction_json: data.extraction_json || null,
        confiance: data.confiance || null,
      };

      const { data: newPaiement, error } = await supabase
        .from('paiements_clients')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;
      return newPaiement as PaiementClient;
    },
    onSuccess: () => {
      toast.success('Paiement enregistré');
      invalidateFinancesChantier(queryClient, entrepriseId!, chantierId!);
      // Notifier la checklist d'onboarding immédiatement
      window.dispatchEvent(new Event('b8ild-checklist-update'));
    },
    onError: (err) => {
      console.error('Erreur création paiement:', err);
      captureError(err instanceof Error ? err : new Error(String(err)), {
        context: 'paiements_chantier_add',
        chantierId,
      });
      toast.error('Erreur lors de la création du paiement');
    },
  });

  /**
   * Mutation : modifier un paiement
   */
  const updatePaiementMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<PaiementFormData> }) => {
      const { error } = await supabase
        .from('paiements_clients')
        .update(data)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Paiement mis à jour');
      invalidateFinancesChantier(queryClient, entrepriseId!, chantierId!);
    },
    onError: (err, variables) => {
      console.error('Erreur mise à jour paiement:', err);
      captureError(err instanceof Error ? err : new Error(String(err)), {
        context: 'paiements_chantier_update',
        paiementId: variables.id,
      });
      toast.error('Erreur lors de la mise à jour');
    },
  });

  /**
   * Mutation : supprimer un paiement
   */
  const deletePaiementMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('paiements_clients')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Paiement supprimé');
      invalidateFinancesChantier(queryClient, entrepriseId!, chantierId!);
    },
    onError: (err, id) => {
      console.error('Erreur suppression paiement:', err);
      captureError(err instanceof Error ? err : new Error(String(err)), {
        context: 'paiements_chantier_delete',
        paiementId: id,
      });
      toast.error('Erreur lors de la suppression');
    },
  });

  // ============================================================================
  // MUTATIONS - AJUSTEMENTS
  // ============================================================================

  /**
   * Mutation : ajouter un ajustement (avenant ou remise)
   */
  const addAjustementMutation = useMutation({
    mutationFn: async (data: AjustementFormData) => {
      const insertData = {
        chantier_id: chantierId!,
        type: data.type,
        montant: Math.abs(data.montant), // Toujours positif
        description: data.description || null,
        date_ajustement: data.date_ajustement || new Date().toISOString().split('T')[0],
      };

      const { data: newAjustement, error } = await supabase
        .from('ajustements_paiement')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;
      return newAjustement as AjustementPaiement;
    },
    onSuccess: (_result, data) => {
      const label = data.type === 'avenant' ? 'Avenant' : 'Remise';
      toast.success(`${label} enregistré(e)`);
      invalidateFinancesChantier(queryClient, entrepriseId!, chantierId!);
    },
    onError: (err, data) => {
      console.error('Erreur création ajustement:', err);
      captureError(err instanceof Error ? err : new Error(String(err)), {
        context: 'paiements_ajustement_add',
        chantierId,
        ajustementType: data.type,
      });
      toast.error('Erreur lors de la création');
    },
  });

  /**
   * Mutation : modifier un ajustement
   */
  const updateAjustementMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<AjustementFormData> }) => {
      const updateData = { ...data };
      if (updateData.montant !== undefined) {
        updateData.montant = Math.abs(updateData.montant);
      }

      const { error } = await supabase
        .from('ajustements_paiement')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Ajustement mis à jour');
      invalidateFinancesChantier(queryClient, entrepriseId!, chantierId!);
    },
    onError: (err, variables) => {
      console.error('Erreur mise à jour ajustement:', err);
      captureError(err instanceof Error ? err : new Error(String(err)), {
        context: 'paiements_ajustement_update',
        ajustementId: variables.id,
      });
      toast.error('Erreur lors de la mise à jour');
    },
  });

  /**
   * Mutation : supprimer un ajustement
   */
  const deleteAjustementMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('ajustements_paiement')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Ajustement supprimé');
      invalidateFinancesChantier(queryClient, entrepriseId!, chantierId!);
    },
    onError: (err, id) => {
      console.error('Erreur suppression ajustement:', err);
      captureError(err instanceof Error ? err : new Error(String(err)), {
        context: 'paiements_ajustement_delete',
        ajustementId: id,
      });
      toast.error('Erreur lors de la suppression');
    },
  });

  // ============================================================================
  // WRAPPERS (préserver les signatures de retour de l'API publique)
  // ============================================================================

  const addPaiement = async (data: PaiementFormData): Promise<PaiementClient | null> => {
    if (!chantierId) {
      toast.error('ID du chantier manquant');
      return null;
    }
    try {
      return await addPaiementMutation.mutateAsync(data);
    } catch {
      return null;
    }
  };

  const updatePaiement = async (id: string, data: Partial<PaiementFormData>): Promise<boolean> => {
    try {
      await updatePaiementMutation.mutateAsync({ id, data });
      return true;
    } catch {
      return false;
    }
  };

  const deletePaiement = async (id: string): Promise<boolean> => {
    try {
      await deletePaiementMutation.mutateAsync(id);
      return true;
    } catch {
      return false;
    }
  };

  const addAjustement = async (data: AjustementFormData): Promise<AjustementPaiement | null> => {
    if (!chantierId) {
      toast.error('ID du chantier manquant');
      return null;
    }
    try {
      return await addAjustementMutation.mutateAsync(data);
    } catch {
      return null;
    }
  };

  const updateAjustement = async (id: string, data: Partial<AjustementFormData>): Promise<boolean> => {
    try {
      await updateAjustementMutation.mutateAsync({ id, data });
      return true;
    } catch {
      return false;
    }
  };

  const deleteAjustement = async (id: string): Promise<boolean> => {
    try {
      await deleteAjustementMutation.mutateAsync(id);
      return true;
    } catch {
      return false;
    }
  };

  // ============================================================================
  // ÉTAT AGRÉGÉ + REFRESH
  // ============================================================================

  const isLoading = paiementsQuery.isLoading || ajustementsQuery.isLoading ||
    totalsQuery.isLoading || facturesQuery.isLoading;

  const error = paiementsQuery.error || ajustementsQuery.error || totalsQuery.error || null;

  const refresh = async () => {
    await Promise.all([
      paiementsQuery.refetch(),
      ajustementsQuery.refetch(),
      totalsQuery.refetch(),
      facturesQuery.refetch(),
    ]);
  };

  // ============================================================================
  // RETURN (API publique identique)
  // ============================================================================

  return {
    // État
    paiements: paiementsQuery.data ?? [],
    ajustements: ajustementsQuery.data ?? [],
    totals: totalsQuery.data ?? null,
    facturesClients: facturesQuery.data ?? [],
    isLoading,
    error,
    // Actions
    addPaiement,
    updatePaiement,
    deletePaiement,
    addAjustement,
    updateAjustement,
    deleteAjustement,
    refresh,
  };
}


// ============================================================================
// HOOK SIMPLE POUR LES TOTAUX UNIQUEMENT
// ============================================================================

/**
 * Hook léger pour récupérer uniquement les totaux de paiement
 * Utile pour les composants qui n'ont besoin que des KPIs
 *
 * Note: Partage la même queryKey que le hook principal — le cache est mutualisé.
 * Le Realtime est géré par usePaiementsChantier quand il est monté sur la même page.
 */
export function usePaiementTotals(chantierId: string | undefined) {
  const { entrepriseId } = useSubscription();

  const { data: totals, isLoading } = useQuery({
    queryKey: queryKeys.finances.paiementTotals(entrepriseId!, chantierId!),
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('get_paiement_totals', { p_chantier_id: chantierId! });

      if (error) throw error;

      if (data && data.length > 0) {
        return data[0] as PaiementTotals;
      }
      return null;
    },
    enabled: !!entrepriseId && !!chantierId,
  });

  return { totals: totals ?? null, isLoading };
}
